import React from "react";
import { Box } from "@mui/material";
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../StyledComponents/styled";

/**
 * Comma-separated names in a table cell.
 * overflowMode "tooltip" (default): +N more shows the remaining names on hover (row height stays put).
 * overflowMode "expand": +N more toggles the rest in the cell.
 *
 * items: [{ key, label, tooltip }] - tooltip is optional and shown on hovering the name
 */
const ExpandableNameList = ({ items = [], maxVisible = 2, emptyText = "-", overflowMode = "tooltip" }) => {
    const [expanded, setExpanded] = React.useState(false);

    const nameList = Array.isArray(items) ? items : [];

    if (nameList.length === 0) {
        return <>{emptyText}</>;
    }

    const useTooltipOverflow = overflowMode === "tooltip";
    // tooltip mode never expands the cell — only the first names stay visible
    const visibleNames = useTooltipOverflow === false && expanded === true ? nameList : nameList.slice(0, maxVisible);
    const hiddenNames = nameList.slice(visibleNames.length);
    const hiddenCount = hiddenNames.length;
    const showOverflow = nameList.length > maxVisible;

    const overflowLabel = useTooltipOverflow === true || expanded !== true
        ? "+" + hiddenCount + " more"
        : "show less";

    const remainingTooltip = (
        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
            {hiddenNames.map((item) => (
                <li key={item.key}>{item.label}</li>
            ))}
        </Box>
    );

    return (
        <Box component="span" sx={{ display: "inline", minWidth: 0, overflowWrap: "anywhere" }}>
            {visibleNames.map((item, index) => {
                const separator = index < visibleNames.length - 1 ? ", " : "";
                // the comma stays outside of the tooltip so only the name reacts to hover
                return (
                    <React.Fragment key={item.key}>
                        {typeof item.tooltip !== "undefined" && item.tooltip !== "" ? (
                            <HtmlTooltip title={item.tooltip}>
                                <Box component="span">{item.label}</Box>
                            </HtmlTooltip>
                        ) : (
                            <Box component="span">{item.label}</Box>
                        )}
                        {separator}
                    </React.Fragment>
                );
            })}

            {showOverflow === true && (
                <>
                    {" "}
                    {useTooltipOverflow === true ? (
                        <HtmlTooltip title={remainingTooltip}>
                            <StyledDarkRedInformationTypography
                                variant="body2"
                                sx={{ display: "inline", cursor: "default", whiteSpace: "nowrap", fontWeight: "normal" }}
                            >
                                {overflowLabel}
                            </StyledDarkRedInformationTypography>
                        </HtmlTooltip>
                    ) : (
                        <StyledDarkRedInformationTypography
                            variant="body2"
                            sx={{ display: "inline", cursor: "pointer", whiteSpace: "nowrap", fontWeight: "normal" }}
                            onClick={(e) => {
                                e.preventDefault();
                                setExpanded(!expanded);
                            }}
                        >
                            {overflowLabel}
                        </StyledDarkRedInformationTypography>
                    )}
                </>
            )}
        </Box>
    );
};

export default ExpandableNameList;
